import {
  wrapCall,
  embedsToQuery,
  callGet,
  callPost,
  callPut,
  callDelete,
  callPatch
} from './helpers'

//* ~~~~~~ TASKS ROUTES ~~~~~~

// Creates a new task
export const postTask = async (data: TaskUpdate) => {
  return (await wrapCall({
    method: callPost,
    uri: '/tasks',
    data,
    rawError: true
  })) as APITask
}

// Updates multiple tasks
export const patchTasks = async (data: APITaskPatch) => {
  return await wrapCall({
    method: callPatch,
    uri: '/tasks',
    data,
    noData: true
  })
}

// Gets specific tasks in the system
export const lookupTasks = async (ids: string[], embed: string[] = []) => {
  return (await wrapCall({
    method: callPost,
    uri: '/tasks/bulk',
    data: { ids, embed }
  })) as APITask[]
}

// Get a single task
export const getTask = async (id: string, embeds: string[] = []) => {
  return (await wrapCall({
    method: callGet,
    uri: `/tasks/${id}${embedsToQuery(embeds)}`
  })) as APITask
}

// Gets tasks with query
export const getTasksWithQuery = async (query: string) => {
  return (await wrapCall({
    method: callGet,
    uri: `/tasks${query}`,
    noData: true
  })) as APITask[]
}

// Edits a task
export const updateTask = async (id: string, data: TaskUpdate) =>
  (await wrapCall({
    method: callPut,
    uri: `/tasks/${id}`,
    data,
    rawError: true
  })) as APITask

// Delete a task
export const deleteTask = async (id: string) =>
  await wrapCall({ method: callDelete, uri: `/tasks/${id}` })

// Add a comment to a task
export const postComment = async (id: string, data: APICommentUpdate) =>
  (await wrapCall({
    method: callPost,
    uri: `/tasks/${id}/comments`,
    data
  })) as APIComment

// Edit a comment on a task
export const putComment = async (
  taskId: string,
  commentId: string,
  data: APICommentUpdate
) =>
  (await wrapCall({
    method: callPut,
    uri: `/tasks/${taskId}/comments/${commentId}`,
    data
  })) as APIComment

// Delete a comment on a task
export const deleteComment = async (taskId: string, commentId: string) => {
  return (await wrapCall({
    method: callDelete,
    uri: `/tasks/${taskId}/comments/${commentId}`
  })) as APIComment
}

// Gets comments for a task
export const getComments = async (id: string) => {
  return (await wrapCall({
    method: callGet,
    uri: `/tasks/${id}/comments`
  })) as APIComment[]
}

// Gets specific comment for a task
export const getComment = async (taskID: string, id: string) => {
  return (await wrapCall({
    method: callGet,
    uri: `/tasks/${taskID}/comments/${id}`
  })) as APIComment
}

//* ~~~~~~ TYPES ~~~~~~

// Gets available task types
export const getTaskTypes = async () => {
  return (await wrapCall({
    method: callGet,
    uri: '/tasks/types'
  })) as TaskType[]
}

// Adds a new task type
export const postTaskType = async (data: Omit<TaskType, 'id'>) => {
  return (await wrapCall({
    method: callPost,
    uri: '/tasks/types',
    data
  })) as TaskType
}

// Deletes a task type by id
export const deleteTaskType = async (id: string) => {
  return await wrapCall({ method: callDelete, uri: `/tasks/types/${id}` })
}

// Updates a task type by id
export const updateTaskType = async (
  id: string,
  data: Omit<TaskType, 'id'>
) => {
  return (await wrapCall({
    method: callPut,
    uri: `/tasks/types/${id}`,
    data
  })) as TaskType
}

//* ~~~~~~ SUBTASK RELATED ~~~~~~

// Gets subtasks for a task
export const getSubtasks = async (
  id: string,
  embeds: string[] = ['sub_tasks'],
  customFieldsToInclude: string = ''
) => {
  return (await wrapCall({
    method: callGet,
    uri: `/tasks/${id}/subtasks${embedsToQuery(
      embeds
    )}&include_closed=true${customFieldsToInclude}&per_page=100`
  })) as APITask[]
}

//* ~~~~~~ CATEGORIES ~~~~~~

// Gets available task categories
export const getTaskCategories = async () => {
  return (await wrapCall({
    method: callGet,
    uri: '/tasks/categories'
  })) as APICategory[]
}

// Create a task category
export const createTaskCategory = async (data: APICategoryUpdate) => {
  return (await wrapCall({
    method: callPost,
    uri: '/tasks/categories',
    data
  })) as APICategory
}

// Delete a task category
export const deleteTaskCategory = async (id: string) => {
  return await wrapCall({ method: callDelete, uri: `/tasks/categories/${id}` })
}

// Update a task category
export const updateTaskCategory = async (
  id: string,
  data: APICategoryUpdate
) => {
  return (await wrapCall({
    method: callPut,
    uri: `/tasks/categories/${id}`,
    data
  })) as APICategory
}

//* ~~~~~~ STATUSES ~~~~~~

// Gets available task statuses
export const getTaskStatuses = async () => {
  return (await wrapCall({
    method: callGet,
    uri: '/tasks/statuses'
  })) as TaskStatus[]
}

// Create a task status
export const createTaskStatus = async (data: TaskStatusUpdate) => {
  return (await wrapCall({
    method: callPost,
    uri: '/tasks/statuses',
    data
  })) as TaskStatus
}

// Delete a task status
export const deleteTaskStatus = async (id: string) => {
  return await wrapCall({ method: callDelete, uri: `/tasks/statuses/${id}` })
}

// Update a task status
export const updateTaskStatus = async (id: string, data: TaskStatusUpdate) => {
  return (await wrapCall({
    method: callPut,
    uri: `/tasks/statuses/${id}`,
    data
  })) as TaskStatus
}

//* ~~~~~~ PRIORITIES ~~~~~~

// Gets available task priorities
export const getTaskPriorities = async () => {
  return (await wrapCall({
    method: callGet,
    uri: '/tasks/priorities'
  })) as TaskPriority[]
}

// Create a task priority
export const createTaskPriority = async (data: APITaskPriorityCreate) => {
  return (await wrapCall({
    method: callPost,
    uri: '/tasks/priorities',
    data
  })) as TaskPriority
}

// Update a task priority
export const updateTaskPriority = async (
  id: string,
  data: APITaskPriorityCreate
) => {
  return (await wrapCall({
    method: callPut,
    uri: `/tasks/priorities/${id}`,
    data
  })) as TaskPriority
}

// Delete a task priority
export const deleteTaskPriority = async (id: string) => {
  return await wrapCall({ method: callDelete, uri: `/tasks/priorities/${id}` })
}

//* ~~~~~~ STATS ~~~~~~

// Gets tasks stats accross the system
export const getGlobalTaskStats = async () => {
  return (await wrapCall({
    method: callGet,
    uri: '/tasks/stats'
  })) as TaskStats
}

//* ~~~~~~ CUSTOM FIELDS ~~~~~~

// Gets task meta for a task id
export const getTaskMeta = async (id: string) => {
  return (await wrapCall({
    method: callGet,
    uri: `/tasks/${id}/meta`
  })) as APIMeta[]
}

// Updates task meta for a task id
export const putTaskMeta = async (id: string, data: APIMeta[]) => {
  return await wrapCall({
    method: callPut,
    uri: `/tasks/${id}/meta`,
    data: { meta: data }
  })
}
